import React from 'react';
import { Helmet } from 'react-helmet-async';
import { IonIcon } from '@ionic/react';
import { documentTextOutline, rocketOutline, statsChartOutline } from 'ionicons/icons';
import PublicNav from 'layouts/PublicNav';
import PublicFooter from 'layouts/PublicFooter';
import useScrollReveal from 'hooks/useScrollReveal';
import './SolutionsPage.css';

const SolutionsPage = () => {
  const pageRef = useScrollReveal();

  return (
    <>
      <Helmet>
        <title>Zibby — the cloud pipeline for Claude Code, Cursor, Codex, and Gemini</title>
        <meta name="description" content="Compose Claude Code, Cursor, Codex, and Gemini into structured workflows with Zod-validated handoff between nodes. Vendor-neutral. JavaScript-first. Cloud-hosted." />
      </Helmet>
      <div className="public-page fade-in" ref={pageRef}>
        <PublicNav />

        {/* Hero */}
        <div className="solutions-hero">
          <svg className="solutions-hero-icon reveal solutions-hero-icon--animated" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path className="icon-draw icon-draw-1" d="M28 4L8 16v24l20 12 20-12V16L28 4z" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <path className="icon-draw icon-draw-2" d="M28 28L8 16m20 12v24m0-24l20-12" stroke="currentColor" strokeWidth="1.5" />
            <circle className="icon-draw icon-draw-3" cx="28" cy="28" r="4" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <h1 className="reveal reveal-delay-1">The cloud pipeline for Claude Code, Cursor, Codex, and Gemini</h1>
          <p className="solutions-hero-subtitle reveal reveal-delay-2">
            Compose them into structured workflows with Zod-validated handoff between nodes. Vendor-neutral. JavaScript-first. Runs locally or in our cloud.
          </p>
          <div className="solutions-hero-actions reveal reveal-delay-3">
            <a href="/connect" className="solutions-hero-btn solutions-hero-btn--primary">Start building</a>
            <a href="https://docs.zibby.app" className="solutions-hero-btn solutions-hero-btn--secondary" target="_blank" rel="noopener noreferrer">Read the docs</a>
          </div>
        </div>

        {/* Feature showcase — the multi-agent graph */}
        <div className="solutions-showcase">
          <div className="solutions-showcase-card">
            <div className="solutions-showcase-left">
              <div className="solutions-showcase-prompt reveal">
                <div className="solutions-showcase-prompt-title">Plan</div>
                Claude Code reads the ticket, builds an implementation plan with Zod-typed sections (files, risks, test cases).
              </div>
              <div className="solutions-showcase-prompt reveal reveal-delay-1">
                <div className="solutions-showcase-prompt-title">Implement</div>
                Cursor takes the plan, edits the repo, returns a structured diff. The plan's Zod schema gates what the next node sees.
              </div>
              <div className="solutions-showcase-prompt reveal reveal-delay-2">
                <div className="solutions-showcase-prompt-title">Verify</div>
                Codex runs the new tests, validates against acceptance criteria, returns a pass/fail report. Triggers a human review on the next CI step.
              </div>
            </div>
            <div className="solutions-showcase-right reveal reveal-delay-3">
              <code>
                <span className="solutions-showcase-code-comment">{'// graph.mjs — your CI/CD pipeline'}</span>{'\n'}
                <span className="solutions-showcase-code-keyword">import</span> {'{ WorkflowGraph, z }'} <span className="solutions-showcase-code-keyword">from</span> <span className="solutions-showcase-code-string">'@zibby/agent-workflow'</span>;{'\n\n'}
                <span className="solutions-showcase-code-keyword">const</span> graph = <span className="solutions-showcase-code-keyword">new</span> <span className="solutions-showcase-code-fn">WorkflowGraph</span>();{'\n\n'}
                graph{'\n'}
                {'  .'}<span className="solutions-showcase-code-fn">addNode</span>(<span className="solutions-showcase-code-string">'plan'</span>, {'{\n'}
                {'    '}agent: <span className="solutions-showcase-code-string">'claude'</span>,{'\n'}
                {'    '}prompt, outputSchema: PlanSchema{'\n'}
                {'  }'}){'\n'}
                {'  .'}<span className="solutions-showcase-code-fn">addNode</span>(<span className="solutions-showcase-code-string">'implement'</span>, {'{\n'}
                {'    '}agent: <span className="solutions-showcase-code-string">'cursor'</span>,{'\n'}
                {'    '}prompt, outputSchema: DiffSchema{'\n'}
                {'  }'}){'\n'}
                {'  .'}<span className="solutions-showcase-code-fn">addNode</span>(<span className="solutions-showcase-code-string">'verify'</span>, {'{\n'}
                {'    '}agent: <span className="solutions-showcase-code-string">'codex'</span>,{'\n'}
                {'    '}prompt, outputSchema: ResultSchema{'\n'}
                {'  }'});{'\n\n'}
                graph.<span className="solutions-showcase-code-fn">addEdge</span>(<span className="solutions-showcase-code-string">'plan'</span>, <span className="solutions-showcase-code-string">'implement'</span>);{'\n'}
                graph.<span className="solutions-showcase-code-fn">addEdge</span>(<span className="solutions-showcase-code-string">'implement'</span>, <span className="solutions-showcase-code-string">'verify'</span>);{'\n'}
                graph.<span className="solutions-showcase-code-fn">setEntryPoint</span>(<span className="solutions-showcase-code-string">'plan'</span>);{'\n\n'}
                <span className="solutions-showcase-code-keyword">export default</span> graph;
              </code>
            </div>
          </div>
        </div>

        {/* Pipeline section — what makes this different */}
        <div className="solutions-pipeline">
          <div className="solutions-pipeline-title reveal">
            <h2>Why coding agents need orchestration you don't own</h2>
          </div>
          <ul className="solutions-pipeline-list">
            <li className="reveal"><strong>Multi-vendor by design.</strong> Mix Claude Code, Codex, Cursor, Gemini in one pipeline. Anthropic will never help you orchestrate Codex. OpenAI will never help you orchestrate Claude. Multi-vendor neutrality is structural, not a feature.</li>
            <li className="reveal reveal-delay-1"><strong>Schema-enforced handoff.</strong> Zod validates every node's output before the next runs. No prompt-stuffing, no "the agent kind of returned something" surprises. Deterministic edges over stochastic agents.</li>
            <li className="reveal reveal-delay-2"><strong>Cloud-hosted CI/CD.</strong> Heroku-style bundles, ~3s cold start. SSE log streaming with auto-reconnect. Runtime caps, rate limits, billing — production primitives, not local IDE demos.</li>
            <li className="reveal"><strong>Session replay.</strong> Every run lands in a stable on-disk format (JSONL events + per-node artifacts). Re-run any node from any past session via <code>--session &lt;id&gt; --node &lt;name&gt;</code>. Studio (desktop) currently visualises test workflow runs; broader workflow support coming.</li>
            <li className="reveal reveal-delay-1"><strong>Static egress IPs.</strong> $50/mo addon pins all outbound HTTPS through one IP your customer can whitelist. Works with self-hosted GitLab, GitHub Enterprise, Salesforce, Oracle Cloud.</li>
            <li className="reveal reveal-delay-2"><strong>JavaScript-first.</strong> Code-first graphs, no YAML, no visual editor. Hot-reload local dev. The same npm ecosystem your team already ships with.</li>
          </ul>
        </div>

        {/* Resources */}
        <div className="solutions-resources">
          <div className="solutions-resources-inner">
            <h2 className="reveal">Get started</h2>
            <div className="solutions-resources-cards">
              <a href="https://docs.zibby.app" className="solutions-resource-card reveal" target="_blank" rel="noopener noreferrer">
                <h3>Compose your first multi-agent pipeline in 60 seconds</h3>
                <div className="solutions-resource-card-footer">
                  <IonIcon icon={documentTextOutline} /> Developer docs
                </div>
              </a>
              <a href="/connect" className="solutions-resource-card reveal reveal-delay-1">
                <h3>Install the CLI, run a workflow locally, deploy to cloud</h3>
                <div className="solutions-resource-card-footer">
                  <IonIcon icon={rocketOutline} /> Quickstart
                </div>
              </a>
              <a href="mailto:support@zibby.app" className="solutions-resource-card reveal reveal-delay-2">
                <h3>Self-hosted GitLab behind a firewall? Talk to us about static-IP egress</h3>
                <div className="solutions-resource-card-footer">
                  <IonIcon icon={statsChartOutline} /> Enterprise
                </div>
              </a>
            </div>
          </div>
        </div>

        <PublicFooter />
      </div>
    </>
  );
};

export default SolutionsPage;
